import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, CreditCard, Landmark, Download, CheckCircle2, FileSpreadsheet, Briefcase, RefreshCw, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const START_YEAR = 2025;
const START_MONTH = 4; // April

function parseMonthKey(mk) {
  if (!mk || mk.length < 7) return null;
  const [y, m] = mk.split('-');
  const mi = parseInt(m) - 1;
  return { year: parseInt(y), month: mi + 1, label: `${MONTHS[mi]}-${y.slice(2)}`, key: mk };
}

function getAvailableFYs(monthKeys) {
  const fys = new Set();
  monthKeys.forEach(mk => {
    const p = parseMonthKey(mk);
    if (!p) return;
    const fy = p.month >= 4 ? `${p.year}-${String(p.year + 1).slice(2)}` : `${p.year - 1}-${String(p.year).slice(2)}`;
    fys.add(fy);
  });
  return Array.from(fys).sort().reverse();
}

function buildMonthGrid(coveredMonths, activeUntil) {
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const endKey = activeUntil || currentKey;
  const items = [];
  let y = START_YEAR, m = START_MONTH;
  while (true) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (key > endKey) break;
    const covered = coveredMonths[key];
    const parsed = parseMonthKey(key);
    if (covered) {
      const gridType = covered.type === 'covered' ? 'covered' : 'available';
      items.push({ ...covered, type: gridType, parsed });
    } else {
      items.push({ type: 'empty', parsed });
    }
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return items.reverse(); // Most recent first
}

function AccountGrid({ accounts, isBankTab, downloadPdf, downloadByFY, isClosed }) {
  return (
    <div className={`bg-white border rounded-md overflow-hidden ${isClosed ? 'border-[#E2E0D8]/70 opacity-80' : 'border-[#E2E0D8]'}`}>
      <table className="w-full">
        <thead>
          <tr className="bg-[#F7F6F2] border-b border-[#E2E0D8]">
            <th className="text-left px-5 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider">Account</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Months</th>
            <th className="px-3 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account, ai) => {
            const covered = account.months_covered || {};
            const allMonths = buildMonthGrid(covered, account.active_until);
            const coveredMonthKeys = Object.keys(covered);
            const availableFYs = getAvailableFYs(coveredMonthKeys);
            const coveredCount = allMonths.filter(m => m.type !== 'empty').length;
            const rows = [];
            for (let i = 0; i < allMonths.length; i += 6) rows.push(allMonths.slice(i, i + 6));
            if (rows.length === 0) return null;
            return rows.map((row, ri) => (
              <tr key={`${account.name}-${ri}`} className={`border-b border-[#E2E0D8] ${ai % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'}`}>
                <td className="px-5 py-2.5 align-top whitespace-nowrap">
                  {ri === 0 && (
                    <div>
                      <div className="flex items-center gap-2">
                        {isBankTab ? <Building2 size={14} className="text-[#284A33] shrink-0" /> : <CreditCard size={14} className="text-[#284A33] shrink-0" />}
                        <span className="text-sm font-medium text-[#1C1C19]">{account.name}</span>
                        {isClosed && account.active_until && (
                          <span className="text-[10px] text-[#999] bg-[#F0F0EC] px-1.5 py-0.5 rounded">Closed {account.active_until}</span>
                        )}
                      </div>
                      <span className="text-[10px] text-[#8A8A80] mt-0.5 block">{coveredCount} months covered</span>
                    </div>
                  )}
                </td>
                {row.map((item, ci) => (
                  <td key={ci} className="px-4 py-2.5">
                    {item.type === 'available' ? (
                      <button onClick={() => downloadPdf(item.id, item.display_name || item.original_filename)}
                        data-testid={`biz-dl-${item.parsed.key}`}
                        className="text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8] hover:underline cursor-pointer transition-colors">
                        {item.parsed.label}
                      </button>
                    ) : item.type === 'covered' ? (
                      <button onClick={() => item.id && downloadPdf(item.id, item.display_name || item.original_filename)}
                        data-testid={`biz-dl-covered-${item.parsed.key}`}
                        title="From bulk statement — click to download parent PDF"
                        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer transition-colors">
                        <CheckCircle2 size={12} />{item.parsed.label}
                      </button>
                    ) : (
                      <span className="text-sm text-[#C5C3BA]" data-testid={`biz-dl-empty-${item.parsed.key}`}>
                        {item.parsed.label}
                      </span>
                    )}
                  </td>
                ))}
                {Array.from({ length: 6 - row.length }).map((_, i) => (
                  <td key={`empty-${i}`} className="px-4 py-2.5"></td>
                ))}
                <td className="px-3 py-2.5 text-right align-top">
                  {ri === 0 && (
                    <Select onValueChange={(fy) => downloadByFY(account, fy)}>
                      <SelectTrigger data-testid={`biz-download-fy-${account.name.replace(/\s+/g, '-')}`}
                        className="h-7 w-[130px] text-xs border-[#284A33]/30 text-[#284A33]">
                        <Download size={12} className="mr-1" /><SelectValue placeholder="Download" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {availableFYs.map(fy => (<SelectItem key={fy} value={fy}>{fy}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}

function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(null); // { provider, month }
  const fileInputRef = { current: null };

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business/invoices`, { withCredentials: true });
      setInvoices(data.invoices || []);
    } catch { setInvoices([]); }
    setLoading(false);
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const fetchFromEmail = async () => {
    setFetching(true);
    try {
      await axios.post(`${API}/business/invoices/fetch`, {}, { withCredentials: true });
      toast.info('Fetching invoices from email... This may take a minute.');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/business/invoices/fetch-status`, { withCredentials: true });
          if (data.status === 'complete') {
            clearInterval(poll);
            setFetching(false);
            const errors = (data.providers || []).filter(p => p.error);
            const msg = `Fetched ${data.total_fetched} invoice(s)${data.total_skipped > 0 ? `, ${data.total_skipped} already existed` : ''}`;
            if (errors.length > 0) {
              toast.warning(`${msg}. ${errors.length} provider(s) had errors.`);
            } else {
              toast.success(msg);
            }
            loadInvoices();
          }
        } catch {}
      }, 3000);
      // Stop polling after 3 minutes max
      setTimeout(() => { clearInterval(poll); setFetching(false); }, 180000);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to start fetch';
      toast.error(detail);
      setFetching(false);
    }
  };

  const downloadInvoice = (inv) => {
    window.open(`${API}/business/invoices/${inv.id}/download`, '_blank');
  };

  const handleUpload = async (file, provider, month) => {
    if (!file) return;
    setUploading({ provider, month });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', provider);
    formData.append('month', month);
    try {
      const { data } = await axios.post(`${API}/business/invoices/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.status === 'skipped') {
        toast.info('This invoice already exists');
      } else {
        toast.success(`Uploaded ${data.filename}`);
        loadInvoices();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(null);
  };

  const triggerUpload = (provider, month) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handleUpload(file, provider, month);
    };
    input.click();
  };

  // Group invoices by provider
  const byProvider = {};
  invoices.forEach(inv => {
    const p = inv.provider || 'Other';
    if (!byProvider[p]) byProvider[p] = {};
    const mk = inv.month || 'unknown';
    if (!byProvider[p][mk]) byProvider[p][mk] = [];
    byProvider[p][mk].push(inv);
  });

  // Build month grid for invoices (Apr 2025 to current)
  const buildInvoiceMonthGrid = (monthMap) => {
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const items = [];
    let y = 2025, m = 4; // Start from April 2025
    while (true) {
      const key = `${y}-${String(m).padStart(2, '0')}`;
      if (key > currentKey) break;
      const mi = m - 1;
      const parsed = { year: y, month: m, label: `${MONTHS[mi]}-${String(y).slice(2)}`, key };
      const invs = monthMap[key] || [];
      items.push({ parsed, invoices: invs, hasInvoice: invs.length > 0 });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return items.reverse(); // Most recent first
  };

  // Known providers (always show in grid even with 0 invoices)
  const KNOWN_PROVIDERS = ['Google Workspace', 'Airtel', 'IFANOW (My Planner)', 'Emirates', 'Air India', 'WeWork'];
  const allProviders = [...new Set([...KNOWN_PROVIDERS, ...Object.keys(byProvider)])].sort();
  const providers = allProviders;

  // Upload for any provider (from top-level button)
  const triggerGeneralUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      // Ask for provider via prompt
      const providerName = window.prompt('Enter vendor name (e.g. WeWork, Airtel, Google Workspace):');
      if (!providerName) return;
      const monthStr = window.prompt('Enter month (YYYY-MM format, e.g. 2025-06):');
      if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) {
        toast.error('Invalid month format. Use YYYY-MM (e.g. 2025-06)');
        return;
      }
      for (const file of files) {
        await handleUpload(file, providerName, monthStr);
      }
    };
    input.click();
  };

  return (
    <div className="space-y-4" data-testid="biz-invoices-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-[#5C5C55]">
          <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Available — click to download</span>
          <span className="flex items-center gap-1"><Download size={12} className="rotate-180 text-[#C5C3BA]" /> Missing — click to upload</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={triggerGeneralUpload} variant="outline" data-testid="biz-invoices-upload-btn"
            className="border-[#E2E0D8] text-[#1C1C19] hover:bg-[#F7F6F2] h-8 px-4 text-xs">
            <Download size={13} className="mr-1.5 rotate-180" /> Upload Invoice
          </Button>
          <Button onClick={fetchFromEmail} disabled={fetching} data-testid="biz-invoices-fetch-btn"
            className="bg-[#284A33] text-white hover:bg-[#1C3624] h-8 px-4 text-xs">
            <RefreshCw size={13} className={`mr-1.5 ${fetching ? 'animate-spin' : ''}`} />
            {fetching ? 'Fetching...' : 'Fetch from Email'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-[#5C5C55]">Loading invoices...</div>
      ) : providers.length === 0 ? (
        <div className="bg-white border border-[#E2E0D8] rounded-md p-10 text-center">
          <FileText size={28} className="mx-auto mb-2 text-[#D5D2C6]" />
          <p className="text-sm text-[#5C5C55]">No invoices fetched yet</p>
          <p className="text-xs text-[#8A8A80] mt-1">Click "Fetch from Email" to download invoices from your email</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F7F6F2] border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider">Vendor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Months</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider, pi) => {
                const monthMap = byProvider[provider];
                const allMonths = buildInvoiceMonthGrid(monthMap);
                const coveredCount = allMonths.filter(m => m.hasInvoice).length;
                const rows = [];
                for (let i = 0; i < allMonths.length; i += 6) rows.push(allMonths.slice(i, i + 6));
                if (rows.length === 0) return null;
                return rows.map((row, ri) => (
                  <tr key={`${provider}-${ri}`} className={`border-b border-[#E2E0D8] ${pi % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'}`}>
                    <td className="px-5 py-2.5 align-top whitespace-nowrap">
                      {ri === 0 && (
                        <div>
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-[#284A33] shrink-0" />
                            <span className="text-sm font-medium text-[#1C1C19]">{provider}</span>
                          </div>
                          <span className="text-[10px] text-[#8A8A80] mt-0.5 block">{coveredCount} invoices</span>
                        </div>
                      )}
                    </td>
                    {row.map((item, ci) => (
                      <td key={ci} className="px-4 py-2.5">
                        {item.hasInvoice ? (
                          <button onClick={() => downloadInvoice(item.invoices[0])}
                            data-testid={`biz-inv-${item.parsed.key}`}
                            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer transition-colors">
                            <CheckCircle2 size={12} />{item.parsed.label}
                          </button>
                        ) : (
                          <button onClick={() => triggerUpload(provider, item.parsed.key)}
                            disabled={uploading?.provider === provider && uploading?.month === item.parsed.key}
                            data-testid={`biz-inv-upload-${item.parsed.key}`}
                            className="inline-flex items-center gap-1 text-sm text-[#C5C3BA] hover:text-[#284A33] hover:underline cursor-pointer transition-colors"
                            title={`Upload invoice for ${item.parsed.label}`}>
                            {uploading?.provider === provider && uploading?.month === item.parsed.key ? (
                              <RefreshCw size={11} className="animate-spin" />
                            ) : (
                              <Download size={11} className="rotate-180" />
                            )}
                            {item.parsed.label}
                          </button>
                        )}
                      </td>
                    ))}
                    {Array.from({ length: 6 - row.length }).map((_, i) => (
                      <td key={`empty-${i}`} className="px-4 py-2.5"></td>
                    ))}
                    <td className="px-3 py-2.5 text-right align-top">
                      {ri === 0 && coveredCount > 0 && (
                        <span className="text-[10px] text-[#8A8A80]">{coveredCount} of {allMonths.length}</span>
                      )}
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BusinessClaimsPanel() {
  const [claimsFY, setClaimsFY] = useState('FY 2025-26');
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const MONTH_LABELS = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'];

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await axios.get(`${API}/transactions/claims-summary`, {
        params: { fy: claimsFY }, withCredentials: true,
      });
      setSummary(data);
    } catch { setSummary(null); }
    setLoadingSummary(false);
  }, [claimsFY]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const downloadMonth = async (monthKey) => {
    try {
      const response = await axios.get(`${API}/transactions/export-monthly`, {
        params: { fy: claimsFY, month: monthKey }, withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Claims_${monthKey}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${monthKey}`);
    } catch { toast.error('Download failed'); }
  };

  const downloadClaims = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/transactions/export-monthly`, {
        params: { fy: claimsFY }, withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Claims_${claimsFY.replace(' ', '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Claims Excel downloaded');
    } catch { toast.error('Download failed'); }
    setDownloading(false);
  };

  const months = summary?.months || [];
  const grandTotal = months.reduce((s, m) => s + (m.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="biz-claims-panel">
      <div className="bg-white border border-[#E2E0D8] rounded-md">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#284A33]/10"><FileSpreadsheet size={18} className="text-[#284A33]" /></div>
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1C1C19]">Business Claims</h3>
              <p className="text-[11px] text-[#5C5C55] mt-0.5">Monthly business expenses from personal accounts — Excel with separate tabs per month</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={claimsFY} onValueChange={setClaimsFY}>
              <SelectTrigger className="w-[130px] h-8 text-xs border-[#E2E0D8]" data-testid="biz-claims-fy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FY 2025-26">FY 2025-26</SelectItem>
                <SelectItem value="FY 2026-27">FY 2026-27</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={downloadClaims} disabled={downloading} data-testid="biz-claims-download-btn"
              className="bg-[#284A33] text-white hover:bg-[#1C3624] h-8 px-4 text-xs">
              <Download size={13} className="mr-1.5" />{downloading ? 'Generating...' : 'Download Excel'}
            </Button>
          </div>
        </div>

        {loadingSummary ? (
          <div className="p-8 text-center text-sm text-[#5C5C55]">Loading summary...</div>
        ) : months.length === 0 ? (
          <div className="p-10 text-center">
            <Briefcase size={28} className="mx-auto mb-2 text-[#D5D2C6]" />
            <p className="text-sm text-[#5C5C55]">No business claims found for {claimsFY}</p>
          </div>
        ) : (
          <table className="w-full" data-testid="biz-claims-summary-table">
            <thead>
              <tr className="bg-[#F7F6F2]/60 border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Month</th>
                <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Entries</th>
                <th className="text-right px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Amount</th>
                <th className="w-10 px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={m.month} className={`border-b border-[#E2E0D8]/60 ${m.count > 0 ? 'hover:bg-[#F7F6F2]/40 cursor-pointer' : 'opacity-50'}`}
                  onClick={() => m.count > 0 && downloadMonth(m.month)}
                  data-testid={`biz-claims-month-${m.month}`}>
                  <td className="px-5 py-2.5">
                    <span className={`text-sm font-medium ${m.count > 0 ? 'text-[#2563EB] hover:underline' : 'text-[#1C1C19]'}`}>{MONTH_LABELS[i]} {m.month.split('-')[0]}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-xs font-mono ${m.count > 0 ? 'text-[#1C1C19]' : 'text-[#D5D2C6]'}`}>{m.count || '—'}</span>
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <span className={`text-sm font-mono ${m.count > 0 ? 'text-[#1C1C19]' : 'text-[#D5D2C6]'}`}>
                      {m.count > 0 ? `₹${m.total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {m.count > 0 && <Download size={13} className="text-[#5C5C55] hover:text-[#284A33] transition-colors inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#F7F6F2]/80 border-t-2 border-[#1C1C19]/15">
                <td className="px-5 py-3"><span className="text-sm font-semibold text-[#1C1C19]">Total</span></td>
                <td className="px-4 py-3 text-center"><span className="text-xs font-mono font-semibold text-[#1C1C19]">{summary?.total_count || 0}</span></td>
                <td className="px-5 py-3 text-right">
                  <span className="text-sm font-mono font-semibold text-[#1C1C19]">
                    ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

export default function BusinessDownloadsPage() {
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState('bank_account');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business/downloads/by-account`, { withCredentials: true });
      setAccounts(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadPdf = async (stmtId, displayName) => {
    try {
      const response = await axios.get(`${API}/statements/${stmtId}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let downloadName = displayName;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) downloadName = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Download failed'); }
  };

  const downloadByFY = async (account, fy) => {
    const covered = account.months_covered || {};
    const monthKeys = Object.keys(covered);
    const filtered = fy === 'all' ? monthKeys : monthKeys.filter(mk => {
      const p = parseMonthKey(mk);
      if (!p) return false;
      const stmtFy = p.month >= 4 ? `${p.year}-${String(p.year + 1).slice(2)}` : `${p.year - 1}-${String(p.year).slice(2)}`;
      return stmtFy === fy;
    });
    // Dedupe by statement ID (bulk statements cover multiple months)
    const seenIds = new Set();
    const uniqueStmts = [];
    for (const mk of filtered) {
      const entry = covered[mk];
      if (entry?.id && !seenIds.has(entry.id)) {
        seenIds.add(entry.id);
        uniqueStmts.push(entry);
      }
    }
    if (uniqueStmts.length === 0) { toast.info('No statements for this FY'); return; }
    for (const s of uniqueStmts) {
      await downloadPdf(s.id, s.display_name || s.original_filename);
      await new Promise(r => setTimeout(r, 500));
    }
    toast.success(`Downloaded ${uniqueStmts.length} statement(s) for ${fy === 'all' ? 'all months' : fy}`);
  };

  const isBankTab = tab === 'bank_account';
  const filteredAccounts = accounts.filter(a => isBankTab ? a.type !== 'credit_card' : a.type === 'credit_card');
  const activeAccounts = filteredAccounts.filter(a => !a.active_until);
  const closedAccounts = filteredAccounts.filter(a => !!a.active_until);

  if (loading) return <div className="p-8 text-center text-[#5C5C55]">Loading...</div>;

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1200px]" data-testid="business-downloads-page">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Business Downloads</h2>
        <p className="text-sm text-[#5C5C55] mt-1">Download password-free copies of your business statements.</p>
      </div>

      <div className="flex gap-1 bg-[#E8E6DE] p-1 rounded-lg w-fit">
        <button data-testid="biz-dl-tab-bank"
          onClick={() => setTab('bank_account')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'bank_account' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Landmark size={15} /> Bank Accounts
        </button>
        <button data-testid="biz-dl-tab-cc"
          onClick={() => setTab('credit_card')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'credit_card' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <CreditCard size={15} /> Credit Cards
        </button>
        <button data-testid="biz-dl-tab-claims"
          onClick={() => setTab('claims')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'claims' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <FileSpreadsheet size={15} /> Claims
        </button>
        <button data-testid="biz-dl-tab-invoices"
          onClick={() => setTab('invoices')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'invoices' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <FileText size={15} /> Invoices
        </button>
      </div>

      {/* Legend */}
      {tab !== 'claims' && tab !== 'invoices' && (
        <div className="flex gap-4 text-xs text-[#5C5C55]">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563EB]" /> Statement PDF</span>
          <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Covered (from bulk statement)</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#F5F5F0]" /> No data</span>
        </div>
      )}

      {tab === 'claims' ? (
        <BusinessClaimsPanel />
      ) : tab === 'invoices' ? (
        <InvoicesPanel />
      ) : filteredAccounts.length === 0 ? (
        <div className="bg-white border border-[#E2E0D8] rounded-md p-12 text-center">
          {isBankTab ? <Landmark className="mx-auto mb-3 text-[#D5D2C6]" size={40} /> : <CreditCard className="mx-auto mb-3 text-[#D5D2C6]" size={40} />}
          <p className="text-[#5C5C55]">No business {isBankTab ? 'bank' : 'credit card'} statements uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {activeAccounts.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Active {isBankTab ? 'Bank Accounts' : 'Credit Cards'}
              </h3>
              <AccountGrid accounts={activeAccounts} isBankTab={isBankTab} downloadPdf={downloadPdf} downloadByFY={downloadByFY} />
            </div>
          )}
          {closedAccounts.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#999] mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#CCC]" />
                Closed {isBankTab ? 'Bank Accounts' : 'Credit Cards'}
              </h3>
              <AccountGrid accounts={closedAccounts} isBankTab={isBankTab} downloadPdf={downloadPdf} downloadByFY={downloadByFY} isClosed />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
